import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import Loader from '../../Shared/Loader/Loader';
import APIConstant from '../../../utils/pathConstants';
import api from '../../../utils/apiClient';
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import DeleteConfirmation from '../../Dialogs/DeleteConfirmation';
import AddEditAttendanceTypeConfiguration from './AddEditAttendanceTypeConfiguration';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const AttendanceTypeConfiguration = () => {
    const { catchResponse } = useCatchResponse();
    const [list, setList] = useState([]);
    const [masterData, setMasterData] = useState({});
    const [recordId, setRecordId] = useState('');
    const [visible, setVisible] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        AttendanceName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [securityUserID, setSecurityUserID] = useState('');
    const [rows, setRowsCount] = useState(10);
    const [first, setFirst] = useState(0);
    const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
    const [deleteId, setDeleteId] = useState('');

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        GetList(loginData?._id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        const _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const onPageChange = (e) => {
        setFirst(e.first);
        setRowsCount(e.rows);
    };

    const GetList = async (createdBy) => {
        try {
            Loader.show();
            await api
                .post(APIConstant.path.getAttendanceTypeConfigurationList, { CreatedBy: createdBy })
                .then((response) => {
                    if (response.success) setList(response.data || []);
                    Loader.hide();
                })
                .catch((err) => {
                    Loader.hide();
                    catchResponse(err);
                });
        } catch (err) {
            catchResponse(err);
        }
    };

    const toggleStatus = async (row) => {
        try {
            Loader.show();
            const nextStatus = row.Status === 'Active' ? 'Inactive' : 'Active';
            await api
                .post(APIConstant.path.updateAttendanceTypeConfigurationStatus, {
                    id: row._id,
                    Status: nextStatus,
                    UpdatedBy: securityUserID,
                })
                .then((res) => {
                    if (res.success) {
                        Toast.success({ message: res.message });
                        GetList(securityUserID);
                    } else {
                        Toast.error({ message: res.message });
                        Loader.hide();
                    }
                })
                .catch((err) => {
                    Loader.hide();
                    catchResponse(err);
                });
        } catch (err) {
            catchResponse(err);
        }
    };

    const DeleteRecord = async () => {
        try {
            Loader.show();
            await api
                .post(APIConstant.path.deleteAttendanceTypeConfiguration, {
                    id: deleteId,
                    DeletedBy: securityUserID,
                })
                .then((res) => {
                    if (res.success) Toast.success({ message: 'Deleted successfully' });
                    else Toast.error({ message: res.message });
                    setDeleteId('');
                    handleClose();
                    GetList(securityUserID);
                })
                .catch((err) => {
                    Loader.hide();
                    setDeleteId('');
                    handleClose();
                    catchResponse(err);
                });
        } catch (err) {
            catchResponse(err);
        }
    };

    const onCancel = () => {
        setVisible(false);
        setRecordId('');
        setIsEdit(false);
        setMasterData({});
        GetList(securityUserID);
    };

    const renderFooter = () => (
        <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
            <Button label="Cancel" className="btn-text-action-secondary" onClick={handleClose} />
            <Button label="Delete" className="btn-text-action-primary" onClick={() => DeleteRecord()} />
        </div>
    );

    const handleClose = () => setIsDeleteConfirmation(false);

    const slotsCountTemplate = (row) => (Array.isArray(row.Slots) ? row.Slots.length : 0);

    const statusBodyTemplate = (row) => (
        <InputSwitch checked={row.Status === 'Active'} onChange={() => toggleStatus(row)} />
    );

    const actionBodyTemplate = (row) => (
        <div className="p-d-flex p-ai-center p-jc-end">
            <Button
                icon="pi pi-pencil"
                className="e-sction-btn p-m-0"
                onClick={() => {
                    setRecordId(row._id);
                    setMasterData(row);
                    setIsEdit(true);
                    setVisible(true);
                }}
            />
            <Button
                icon="pi pi-trash"
                className="e-sction-btn p-m-0 p-m-2"
                onClick={() => {
                    setIsDeleteConfirmation(true);
                    setDeleteId(row._id);
                }}
            />
        </div>
    );

    const srNoBodyTemplate = (_data, props) => props.rowIndex + 1;

    return (
        <div className="wayment-owner-dashboard et-master-page">
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-clock"></i>
                    <h2>Attendance Type</h2>
                </div>
                <div className="p-d-flex p-ai-center p-jc-between">
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            placeholder="Global Search"
                        />
                    </div>
                    <Button icon="pi pi-plus" className="e-h-btn" onClick={() => setVisible(true)} />
                </div>
            </div>
            <DataTable
                value={list}
                paginator
                rows={rows}
                first={first}
                onPage={onPageChange}
                filters={filters}
                globalFilterFields={['AttendanceName']}
                responsiveLayout="scroll"
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
                rowsPerPageOptions={[5, 10, 20]}
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                className='custom-table'
                scrollHeight='calc(100vh - 120px)'
                size='small'
            >
                <Column field="id" body={srNoBodyTemplate} header="Sr No." />
                <Column field="AttendanceName" header="Attendance Name" />
                <Column body={slotsCountTemplate} header="Slots" />
                <Column body={statusBodyTemplate} header="Status" />
                <Column body={actionBodyTemplate} header="Action" />
            </DataTable>
            <Dialog
                header={recordId ? 'Edit Attendance Type' : 'Add Attendance Type'}
                className="dialog-popup"
                visible={visible}
                onHide={() => onCancel()}
                draggable={false}
                position="center"
                style={{ width: '900px' }}
            >
                <AddEditAttendanceTypeConfiguration
                    masterData={masterData}
                    isEdit={isEdit}
                    ID={recordId}
                    onCancel={onCancel}
                />
            </Dialog>
            <Dialog
                visible={isDeleteConfirmation}
                onHide={handleClose}
                header={
                    <div className="p-d-flex p-jc-between p-ai-center">
                        <span>Delete Confirmation</span>
                    </div>
                }
                footer={renderFooter()}
                draggable={false}
                dismissableMask
                position="center"
                style={{ width: '450px' }}
            >
                <DeleteConfirmation />
            </Dialog>
        </div>
    );
};

export default AttendanceTypeConfiguration;
